import { AxiosRequestConfig } from 'axios'

import { CreateVoucherPayload, UpdateVoucherData } from './types'

const appendCommonVoucherFields = (
  payload: FormData,
  voucher: Partial<CreateVoucherPayload>,
): void => {
  const {
    title,
    listingTitle,
    shortTitle,
    icon,
    image,
    active,
    pushMessage,
    pushTitle,
    pushDescription,
    notificationId,
    tagsSelected,
    excludedTagsSelected,
    additionalText,
    allTagsAreRequired,
    preloadedEmails,
    tucanCategory,
    toteatCategory,
    storesEnabled,
    tucanEnabled,
    toteatEnabled,
    skus,
    toteatBenefitId,
    visitsValue,
    visitsOperator,
    dateFrom,
    dateTo,
    rewardInverted,
    rewardId,
    timeRestriction,
    prizeStartDate,
    prizeExpiration,
    activePopUp,
    popUpImageId,
  } = voucher

  if (title) payload.append('title', title)
  if (listingTitle) payload.append('listingTitle', listingTitle)
  if (shortTitle) payload.append('shortTitle', shortTitle)
  if (icon) payload.append('icon', icon)
  if (image) payload.append('image', image)
  if (active !== undefined) payload.append('active', active.toString())
  if (pushMessage !== undefined) payload.append('pushMessage', pushMessage.toString())
  if (pushTitle) payload.append('pushTitle', pushTitle)
  if (pushDescription) payload.append('pushDescription', pushDescription)
  if (notificationId !== undefined && notificationId !== null) {
    payload.append('notificationId', notificationId.toString())
  }
  if (tagsSelected) payload.append('tags', tagsSelected.map(el => el.value).join(','))
  if (excludedTagsSelected) {
    payload.append('excludedTags', excludedTagsSelected.map(el => el.value).join(','))
  }
  if (additionalText) payload.append('additionalText', additionalText)
  if (allTagsAreRequired !== undefined) {
    payload.append('allTagsAreRequired', allTagsAreRequired.toString())
  }
  if (preloadedEmails !== undefined) payload.append('preloadedEmails', preloadedEmails)
  if (tucanCategory) payload.append('tucanCategory', tucanCategory)
  if (toteatCategory) payload.append('toteatCategory', toteatCategory)
  if (storesEnabled !== undefined) payload.append('storesEnabled', storesEnabled.toString())
  if (tucanEnabled !== undefined) payload.append('tucanEnabled', tucanEnabled.toString())
  if (toteatEnabled !== undefined) payload.append('toteatEnabled', toteatEnabled.toString())
  if (skus) payload.append('skus', skus.join(','))
  if (toteatBenefitId) payload.append('toteatBenefitId', toteatBenefitId)
  if (visitsValue) payload.append('visitsValue', visitsValue.toString())
  if (visitsOperator) payload.append('visitsOperator', visitsOperator.toString())
  if (dateFrom) payload.append('dateFrom', dateFrom.toString())
  if (dateTo) payload.append('dateTo', dateTo.toString())
  if (rewardId) payload.append('rewardId', rewardId.toString())
  if (rewardInverted) payload.append('rewardInverted', rewardInverted.toString())
  if (timeRestriction) {
    payload.append('timeRestrictionEnabled', timeRestriction.timeRestrictionEnabled.toString())
    if (timeRestriction.allowedDays && timeRestriction.allowedDays.length > 0) {
      payload.append('allowedDays', JSON.stringify(timeRestriction.allowedDays))
    }
    if (timeRestriction.startTime) payload.append('startTime', timeRestriction.startTime)
    if (timeRestriction.endTime) payload.append('endTime', timeRestriction.endTime)
  }
  if (prizeStartDate) payload.append('startDate', prizeStartDate)
  if (prizeExpiration) payload.append('expiration', prizeExpiration)
  if (activePopUp !== undefined) payload.append('activePopUp', activePopUp.toString())
  if (popUpImageId !== undefined) payload.append('popUpImageId', popUpImageId || '')
}

const getVouchers = (): AxiosRequestConfig => ({
  url: '/vouchers',
  method: 'GET',
})

const getVoucher = (id: number): AxiosRequestConfig => ({
  url: `/vouchers/${id}`,
  method: 'GET',
})

const createVoucher = (voucher: CreateVoucherPayload): AxiosRequestConfig => {
  const payload = new FormData()
  appendCommonVoucherFields(payload, voucher)

  return {
    url: '/vouchers',
    method: 'POST',
    data: payload,
  }
}

const updateVoucher = (data: UpdateVoucherData): AxiosRequestConfig => {
  const payload = new FormData()
  appendCommonVoucherFields(payload, data.payload)

  return {
    url: `/vouchers/${data.id}`,
    method: 'PUT',
    data: payload,
  }
}

const deleteVoucher = (id: number): AxiosRequestConfig => ({
  url: `/vouchers/${id}`,
  method: 'DELETE',
})

const getVoucherUsers = (voucherId: number): AxiosRequestConfig => ({
  url: `/vouchers/${voucherId}/users`,
  method: 'GET',
})

const uploadPopUpImage = (file: File): AxiosRequestConfig => {
  const payload = new FormData()
  payload.append('popUpImage', file)

  return {
    url: '/vouchers/pop-up-image',
    method: 'POST',
    data: payload,
  }
}

export {
  getVouchers,
  getVoucher,
  createVoucher,
  updateVoucher,
  deleteVoucher,
  getVoucherUsers,
  uploadPopUpImage,
}
